import { useState } from "react";
import { Download, FileText, Apple, Smartphone, Tablet, ExternalLink } from "lucide-react";
import { PageHeader, Card } from "./AdminUI";

const BASE = "/store-assets";
const PAGES = [["01-home", "Home"], ["02-live-radio", "Live Radio"], ["03-live-tv", "Live TV"], ["04-football", "Football"], ["05-news", "News"], ["06-pricing", "Pricing"]];
const SETS = [
  { id: "ios-iphone-6.5", store: "App Store", label: 'iPhone 6.5"', size: "1284 × 2778", icon: Apple, required: true },
  { id: "ios-ipad-13", store: "App Store", label: 'iPad 13"', size: "2064 × 2752", icon: Tablet, required: true },
  { id: "android-phone", store: "Google Play", label: "Phone", size: "1080 × 1920", icon: Smartphone, required: true },
  { id: "android-tablet-7", store: "Google Play", label: '7" tablet', size: "1200 × 1920", icon: Tablet },
  { id: "android-tablet-10", store: "Google Play", label: '10" tablet', size: "1600 × 2560", icon: Tablet },
];

const GUIDE = [
  ["Apple App Store", ["Open App Store Connect → your app → the version → Previews and Screenshots.", 'Upload the iPhone 6.5" set (1284×2778) and the iPad 13" set (2064×2752), 1–10 images each.', "Keep the order 01 → 06. The first 3 appear in search results.", "The files have no transparency (Apple rejects alpha) and show the real live app."]],
  ["Google Play", ["Open Play Console → Grow → Store presence → Main store listing → Graphics.", "Phone: upload all 6 images (1080×1920). Use 4 or more so the app can be promoted.", '7" and 10" tablet: upload their folders. Upload the feature graphic (1024×500).', "Files are 24-bit PNG, every side 320–3840 px, aspect ratio within 2:1."]],
];

export default function AdminStoreAssets() {
  const [tab, setTab] = useState(SETS[0].id);
  const set = SETS.find((s) => s.id === tab);
  return (
    <div>
      <PageHeader title="App Store Screenshots" desc="Ready-to-upload screenshots for Apple App Store and Google Play, taken from the live app.">
        <a href={`${BASE}/bbkigali-store-screenshots.zip`} download data-testid="store-zip-download"
          className="h-10 px-4 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5 bg-[#E11D2A] hover:bg-[#C8121F] text-white btn-glow"><Download className="w-4 h-4" /> Download all (ZIP)</a>
        <a href={`${BASE}/STORE_SUBMISSION_GUIDE.md`} download data-testid="store-guide-download"
          className="h-10 px-4 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5 bg-white/5 border border-[#273042] text-white hover:bg-white/10"><FileText className="w-4 h-4" /> Guidance document</a>
      </PageHeader>

      <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="store-guidance">
        {GUIDE.map(([title, steps]) => (
          <Card key={title} className="p-5">
            <h3 className="text-white font-bold mb-2">{title}</h3>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-300">{steps.map((s) => <li key={s}>{s}</li>)}</ol>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {SETS.map((s) => (
          <button key={s.id} onClick={() => setTab(s.id)} data-testid={`store-tab-${s.id}`}
            className={`h-10 px-4 rounded-full text-sm font-semibold border inline-flex items-center gap-2 transition-colors ${tab === s.id ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#0A0C10] border-[#273042] text-slate-300 hover:text-white"}`}>
            <s.icon className="w-4 h-4" /> {s.store} · {s.label}{s.required && <span className="text-[9px] uppercase font-extrabold bg-white/15 px-1.5 rounded">Required</span>}
          </button>
        ))}
      </div>

      <Card className="p-5">
        <div className="text-sm text-slate-400 mb-4" data-testid="store-set-size">{set.store} · {set.label} · <b className="text-white">{set.size} px</b> · PNG, no transparency</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {PAGES.map(([file, label]) => {
            const url = `${BASE}/${set.id}/${file}.png`;
            return (
              <div key={file} className="group" data-testid={`store-shot-${set.id}-${file}`}>
                <a href={url} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-[#273042] bg-black">
                  <img src={url} alt={`${label} ${set.label}`} loading="lazy" className="w-full h-auto group-hover:opacity-90 transition-opacity" />
                </a>
                <div className="flex items-center justify-between mt-1.5 text-xs">
                  <span className="text-white font-semibold">{label}</span>
                  <a href={url} download className="text-[#E11D2A] inline-flex items-center gap-1 hover:underline" data-testid={`store-dl-${set.id}-${file}`}><Download className="w-3 h-3" /> PNG</a>
                </div>
              </div>
            );
          })}
        </div>
        <a href={`${BASE}/google-play-feature-graphic-1024x500.png`} target="_blank" rel="noreferrer" data-testid="store-feature-graphic"
          className="mt-5 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><ExternalLink className="w-4 h-4" /> Google Play feature graphic (1024 × 500)</a>
      </Card>
    </div>
  );
}
